import {
  lazy,
  Suspense,
  useCallback,
  useEffect,
  useLayoutEffect,
  useState,
  type ReactNode,
} from 'react';
import { Outlet } from '@tanstack/react-router';
import { useImpersonation } from '../../features/auth/api.js';
import {
  CallStatusBar,
  callStatusBarHeight,
  useCallStatusBarVisible,
} from '../../features/voice/CallStatusBar.js';
import { ImpersonationBanner } from './ImpersonationBanner.js';
import { TopBar } from './TopBar.js';
import { setShellFrame } from './shellFrame.js';

// DialerWidget pulls in @twilio/voice-sdk (~50 KB gzip). Lazy-load so
// the initial bundle stays under the 200 KB budget; users without
// voice configured never download the SDK (the widget hides itself
// when /api/voice/token returns 503).
const DialerWidget = lazy(() =>
  import('../../features/voice/DialerWidget.js').then((m) => ({
    default: m.DialerWidget,
  })),
);

const WorkspaceTabs = lazy(() =>
  import('./WorkspaceTabs.js').then((m) => ({
    default: m.WorkspaceTabs,
  })),
);

const Sidebar = lazy(() =>
  import('./Sidebar.js').then((m) => ({
    default: m.Sidebar,
  })),
);

const WebsiteActivityHealthBanner = lazy(() =>
  import('../../features/website-activity/health.js').then(m => ({ default: m.WebsiteActivityHealthBanner })),
);

const LS_KEY = 'yrp_sidebar_collapsed';
const MOBILE_BP = 768;
const WORKSPACE_TABS_HEIGHT = 43;

function useIsMobile(): boolean {
  const [isMobile, setIsMobile] = useState(() =>
    typeof window !== 'undefined' ? window.innerWidth <= MOBILE_BP : false,
  );
  useEffect(() => {
    const mq = window.matchMedia(`(max-width: ${MOBILE_BP}px)`);
    const handler = (e: MediaQueryListEvent) => setIsMobile(e.matches);
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, []);
  return isMobile;
}

// Persistent app chrome — mounts once via the `/_app` layout route so
// sidebar / topbar / backdrop / main survive navigations between
// authed pages. Pages publish their topbar title + right-aligned
// actions via the `<Shell>` wrapper, which just writes into the
// shell-frame store; only the TopBar subscribes.
export function AppLayout({ children }: { children?: ReactNode }) {
  const isMobile = useIsMobile();
  const callStatusVisible = useCallStatusBarVisible();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [collapsed, setCollapsed] = useState(() => {
    try { return localStorage.getItem(LS_KEY) === 'true'; } catch { return false; }
  });
  const impersonation = useImpersonation();
  const isImpersonating = impersonation.data != null;

  const toggleCollapse = useCallback(() => {
    setCollapsed((c) => {
      const next = !c;
      try { localStorage.setItem(LS_KEY, String(next)); } catch { /* ignore */ }
      return next;
    });
  }, []);

  useEffect(() => {
    if (!isMobile) setMobileOpen(false);
  }, [isMobile]);

  const sidebarWidth = isMobile ? 0 : collapsed ? 64 : 260;
  const topOffset = isImpersonating ? 28 : 0;
  const callStatusOffset = callStatusVisible ? callStatusBarHeight : 0;
  const workspaceTopOffset = topOffset + callStatusOffset;
  const mainTopOffset = workspaceTopOffset + WORKSPACE_TABS_HEIGHT;

  useEffect(() => {
    const root = document.documentElement;
    root.classList.add('has-workspace-chrome');
    root.style.setProperty('--workspace-modal-left', `${sidebarWidth}px`);
    root.style.setProperty(
      '--workspace-modal-top',
      `calc(var(--topbar-height) + ${mainTopOffset}px)`,
    );
    return () => {
      root.classList.remove('has-workspace-chrome');
      root.style.removeProperty('--workspace-modal-left');
      root.style.removeProperty('--workspace-modal-top');
    };
  }, [mainTopOffset, sidebarWidth]);

  return (
    <div
      className={`app-shell bg-paper${isImpersonating ? ' has-impersonation-banner' : ''}`}
      style={{ minHeight: '100vh' }}
    >
      {isImpersonating ? <ImpersonationBanner /> : null}
      <div className="glass-backdrop" aria-hidden="true" />
      <Suspense fallback={<aside className="sidebar-shell glass-sidebar" style={{ width: sidebarWidth === 0 ? 260 : sidebarWidth }} />}>
        <Sidebar
          mobileOpen={mobileOpen}
          onMobileClose={() => setMobileOpen(false)}
          collapsed={collapsed}
          sidebarWidth={sidebarWidth}
        />
      </Suspense>
      <TopBar
        onMenuClick={() => setMobileOpen(true)}
        onToggleCollapse={toggleCollapse}
        collapsed={collapsed}
        sidebarWidth={sidebarWidth}
      />
      <CallStatusBar sidebarWidth={sidebarWidth} topOffset={topOffset} />
      <Suspense fallback={null}>
        <WorkspaceTabs sidebarWidth={sidebarWidth} topOffset={workspaceTopOffset} />
      </Suspense>
      <main
        className="main-content"
        style={{
          marginLeft: sidebarWidth,
          marginTop: mainTopOffset > 0
            ? `calc(var(--topbar-height) + ${mainTopOffset}px)`
            : 'var(--topbar-height)',
          minHeight: mainTopOffset > 0
            ? `calc(100vh - var(--topbar-height) - ${mainTopOffset}px)`
            : 'calc(100vh - var(--topbar-height))',
          padding: isMobile ? 16 : 32,
          transition: 'margin-left 250ms ease, margin-top 180ms ease',
        }}
      >
        <Suspense fallback={null}><WebsiteActivityHealthBanner /></Suspense>
        {children ?? <Outlet />}
      </main>
      {/* Floating call widget. Auto-hides when the voice token endpoint
          returns 503 (voice not configured), so nothing shows until DO
          has TWILIO_API_KEY_SID + _SECRET + TWILIO_TWIML_APP_SID set.
          Lazy-loaded so the Twilio Voice SDK stays out of the initial
          bundle; Suspense fallback is null because the widget is a
          floating overlay — nothing to show while it's loading. */}
      <Suspense fallback={null}>
        <DialerWidget />
      </Suspense>
    </div>
  );
}

interface ShellProps {
  title?: string;
  actions?: ReactNode;
  children: ReactNode;
}

// Back-compat wrapper. Pages keep calling `<Shell title="X">...</Shell>`
// and get the same visible layout, but the chrome is now mounted once
// by `AppLayout` — this component only publishes the frame and renders
// its children inline. Swapping routes no longer tears down the
// sidebar / topbar / backdrop.
export function Shell({ title, actions, children }: ShellProps) {
  useLayoutEffect(() => {
    setShellFrame({ title, actions });
  }, [title, actions]);
  return <>{children}</>;
}
